import { useMemo, useState } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useQuery } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { getEmployees } from '@/lib/employees';
import type { EmployeeListItem } from '@/types/employee';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';

function getInitials(firstName?: string | null, lastName?: string | null): string {
  return `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase() || '?';
}

interface RecipientComboboxProps {
  value: EmployeeListItem | null;
  onChange: (employee: EmployeeListItem | null) => void;
  disabled?: boolean;
}

/**
 * Single-employee search/select used by the manual "Send form" dialog. Active
 * employees only — a pre-boarding form is sent to a current hire with a work
 * email (the backend rejects employees without one).
 */
export function RecipientCombobox({ value, onChange, disabled }: RecipientComboboxProps) {
  const formatName = useEmployeeNameFormatter();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isLoading } = useQuery({
    queryKey: ['employees', 'preboarding-recipient-search', debouncedSearch],
    queryFn: () =>
      getEmployees({
        search: debouncedSearch || undefined,
        status: 'active',
        limit: 50,
        page: 1,
      }),
    enabled: open,
  });

  const employees = useMemo(() => data?.data ?? [], [data]);

  const handleSelect = (employee: EmployeeListItem) => {
    onChange(employee);
    setOpen(false);
    setSearch('');
  };

  return (
    // Portalled out of the dialog, which now scrolls itself and would clip an inline
    // list; `modal` keeps the list wheel-scrollable under the dialog's scroll lock.
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-9 font-normal cursor-pointer"
          disabled={disabled}
        >
          {value ? (
            <span className="flex items-center gap-2 min-w-0">
              <Avatar className="h-5 w-5">
                {value.avatarUrl && <AvatarImage src={value.avatarUrl} />}
                <AvatarFallback className="text-[9px]">
                  {getInitials(value.firstName, value.lastName)}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">
                {formatName({ firstName: value.firstName, lastName: value.lastName })}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">Select an employee…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search by name or email…" value={search} onValueChange={setSearch} />
          <CommandList className="max-h-56">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : employees.length === 0 ? (
              <CommandEmpty className="py-4 text-sm text-muted-foreground text-center">
                {debouncedSearch ? 'No results.' : 'Type to search…'}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {employees.map((employee) => (
                  <CommandItem
                    key={employee.id}
                    value={employee.id}
                    onSelect={() => handleSelect(employee)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value?.id === employee.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <Avatar className="h-6 w-6 mr-2">
                      {employee.avatarUrl && <AvatarImage src={employee.avatarUrl} />}
                      <AvatarFallback className="text-[10px]">
                        {getInitials(employee.firstName, employee.lastName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col min-w-0">
                      <span className="truncate">
                        {formatName({ firstName: employee.firstName, lastName: employee.lastName })}
                      </span>
                      <span className="text-xs text-muted-foreground truncate">
                        {employee.workEmail}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
